import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Check, Loader2, Send, X } from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import { FORMSPREE_ENDPOINT, whatsappHref } from '@/lib/contact'

type Status = 'idle' | 'submitting' | 'success' | 'error'

/**
 * Real inquiry form, opened from every calculator tab's "Dodaj ovo u upit"
 * CTA. Posts straight to the same Formspree endpoint the live site's
 * `#contactForm` already uses (index.html) — ahead of Phase 6's full
 * `#kontakt` section, this is a real, working lead channel rather than the
 * previous WhatsApp-only deep link. The calculator's computed summary is
 * included as a hidden `izracun` field (same field name as the live site)
 * and also shown read-only above the form so the person can see exactly
 * what's being sent.
 *
 * Native <dialog> for focus trap + Escape-to-close, same pattern as
 * portfolio/Lightbox.tsx. Status resets to 'idle' every time it opens so a
 * previous success/error doesn't linger into the next open.
 */
export function InquiryModal({
  open,
  onClose,
  summary,
}: {
  open: boolean
  onClose: () => void
  summary: string[]
}) {
  const { t } = useLanguage()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [status, setStatus] = useState<Status>('idle')

  useEffect(() => {
    const el = dialogRef.current
    if (!el) return
    if (open && !el.open) {
      setStatus('idle')
      el.showModal()
      document.body.style.overflow = 'hidden'
    } else if (!open && el.open) {
      el.close()
      document.body.style.overflow = ''
    }
  }, [open])

  useEffect(() => {
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  const summaryText = summary.join('\n')

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setStatus('submitting')
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: new FormData(e.currentTarget),
        headers: { Accept: 'application/json' },
      })
      setStatus(res.ok ? 'success' : 'error')
    } catch {
      setStatus('error')
    }
  }

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose()
      }}
      className="glow-ring m-auto w-full max-w-lg overflow-visible rounded-lg border border-white/10 bg-background-elevated p-0 text-foreground backdrop:bg-background/90 backdrop:backdrop-blur-sm"
    >
      <div className="relative max-h-[85vh] overflow-y-auto p-6 md:p-7">
        <button
          type="button"
          onClick={onClose}
          aria-label={t('inquiry-modal-close')}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-white/5 text-foreground-muted transition-colors hover:border-accent-fire hover:text-accent-fire"
        >
          <X size={16} />
        </button>

        {status === 'success' ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-fire/15 text-accent-fire">
              <Check size={24} strokeWidth={2.5} />
            </div>
            <h3 className="font-serif text-xl font-semibold text-foreground">{t('form-success-title')}</h3>
            <p className="max-w-sm text-sm leading-relaxed text-foreground-muted">{t('form-success-sub')}</p>
            <a
              href={whatsappHref(t('inquiry-modal-title'))}
              target="_blank"
              rel="noopener"
              className="mt-2 inline-flex items-center gap-2 rounded-pill bg-whatsapp px-5 py-2 text-sm font-bold text-white transition-transform hover:scale-105"
            >
              {t('form-success-wa')}
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <h3 className="pr-8 font-serif text-xl font-semibold text-foreground">{t('inquiry-modal-title')}</h3>
              <p className="mt-1 text-sm text-foreground-muted">{t('inquiry-modal-sub')}</p>
            </div>

            <div>
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-subtle">
                {t('form-calc-label')}
              </span>
              <pre className="max-h-32 overflow-y-auto whitespace-pre-wrap rounded-lg border border-white/10 bg-white/[0.03] p-3 font-sans text-xs leading-relaxed text-foreground-muted">
                {summaryText}
              </pre>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="inquiry-fname" className="text-xs font-semibold text-foreground-subtle">
                  {t('form-fname')}
                </label>
                <input
                  id="inquiry-fname"
                  name="ime"
                  type="text"
                  required
                  autoComplete="given-name"
                  placeholder={t('form-fname-ph')}
                  className="rounded-lg border border-white/15 bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-accent-fire"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="inquiry-lname" className="text-xs font-semibold text-foreground-subtle">
                  {t('form-lname')}
                </label>
                <input
                  id="inquiry-lname"
                  name="prezime"
                  type="text"
                  required
                  autoComplete="family-name"
                  placeholder={t('form-lname-ph')}
                  className="rounded-lg border border-white/15 bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-accent-fire"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="inquiry-email" className="text-xs font-semibold text-foreground-subtle">
                {t('form-email')}
              </label>
              <input
                id="inquiry-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                placeholder={t('form-email-ph')}
                className="rounded-lg border border-white/15 bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-accent-fire"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="inquiry-msg" className="text-xs font-semibold text-foreground-subtle">
                {t('form-msg')}
              </label>
              <textarea
                id="inquiry-msg"
                name="poruka"
                rows={3}
                placeholder={t('form-msg-ph')}
                className="resize-none rounded-lg border border-white/15 bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-accent-fire"
              />
            </div>

            {/* Same field name as the live site (`izracun`) — carries the exact computed summary. */}
            <input type="hidden" name="izracun" value={summaryText} />
            {/* Honeypot anti-spam field, same convention as the live site. */}
            <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

            {status === 'error' && (
              <p className="rounded-lg border border-error/40 bg-error/10 px-3 py-2 text-xs leading-relaxed text-error">
                {t('form-error')}
              </p>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="mt-1 inline-flex items-center justify-center gap-2 rounded-pill bg-gradient-to-r from-accent-amber-light to-accent-amber px-6 py-2.5 text-sm font-bold uppercase tracking-wide text-accent-amber-foreground transition-all duration-normal hover:scale-105 hover:shadow-[0_0_20px_rgba(255,185,99,0.4)] active:scale-95 disabled:pointer-events-none disabled:opacity-60"
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> {t('form-submitting')}
                </>
              ) : (
                <>
                  <Send size={15} /> {t('form-submit')}
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </dialog>
  )
}
